import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import './FranchiseeInformation.css';

function FranchiseeInformation({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Existing fields from Franchisees collection
    name: '',
    email: '',
    phone: '',
    
    // New fields for invoicing
    businessName: '',
    whatsapp: '',
    physicalAddress: '',
    city: '',
    postalCode: '',
    
    // Banking details (optional)
    includeBankingDetails: false,
    bankName: '',
    accountName: '',
    accountNumber: '',
    branchCode: '',
    referenceFormat: '{studentName}+{schoolName}',

    // Statement Settings (NEW)
    statementTitle: '',
    statementSubtitle: '',
    showFranchiseeNameOnStatement: true,
    showFranchiseeEmailOnStatement: false,
    showFranchiseePhoneOnStatement: true,
  });

  useEffect(() => {
    loadFranchiseeData();
  }, []);

  const loadFranchiseeData = async () => {
    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);
      const franchiseeDoc = await getDoc(franchiseeRef);

      if (franchiseeDoc.exists()) {
        const data = franchiseeDoc.data();
        setFormData({
          // Existing fields
          name: data.name || '',
          email: data.email || user.email || '',
          phone: data.phone || '',
          
          // Invoice-specific fields
          businessName: data.businessName || data.name || '',
          whatsapp: data.whatsapp || data.phone || '',
          physicalAddress: data.physicalAddress || '',
          city: data.city || '',
          postalCode: data.postalCode || '',
          
          // Banking details
          includeBankingDetails: data.includeBankingDetails || false,
          bankName: data.bankName || '',
          accountName: data.accountName || '',
          accountNumber: data.accountNumber || '',
          branchCode: data.branchCode || '',
          referenceFormat: data.referenceFormat || '{studentName}+{schoolName}',

          // Statement Settings
          statementTitle: data.statementTitle || '',
          statementSubtitle: data.statementSubtitle || '',
          showFranchiseeNameOnStatement: data.showFranchiseeNameOnStatement !== undefined ? data.showFranchiseeNameOnStatement : true,
          showFranchiseeEmailOnStatement: data.showFranchiseeEmailOnStatement || false,
          showFranchiseePhoneOnStatement: data.showFranchiseePhoneOnStatement !== undefined ? data.showFranchiseePhoneOnStatement : true,
        });
      }
    } catch (error) {
      console.error('Error loading franchisee data:', error);
      alert('Error loading your information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      alert('Please enter your name');
      return;
    }
    
    if (!formData.email.trim()) {
      alert('Please enter your email');
      return;
    }

    if (formData.includeBankingDetails) {
      if (!formData.bankName.trim() || !formData.accountName.trim() || 
          !formData.accountNumber.trim() || !formData.branchCode.trim()) {
        alert('Please complete all banking details or uncheck "Include Banking Details"');
        return;
      }
    }

    setSaving(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, 'Franchisees', user.uid);

      await updateDoc(franchiseeRef, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        businessName: formData.businessName.trim(),
        whatsapp: formData.whatsapp.trim(),
        physicalAddress: formData.physicalAddress.trim(),
        city: formData.city.trim(),
        postalCode: formData.postalCode.trim(),
        includeBankingDetails: formData.includeBankingDetails,
        bankName: formData.bankName.trim(),
        accountName: formData.accountName.trim(),
        accountNumber: formData.accountNumber.trim(),
        branchCode: formData.branchCode.trim(),
        referenceFormat: formData.referenceFormat,
        // Statement Settings
        statementTitle: formData.statementTitle.trim(),
        statementSubtitle: formData.statementSubtitle.trim(),
        showFranchiseeNameOnStatement: formData.showFranchiseeNameOnStatement,
        showFranchiseeEmailOnStatement: formData.showFranchiseeEmailOnStatement,
        showFranchiseePhoneOnStatement: formData.showFranchiseePhoneOnStatement,
        updatedAt: new Date()
      });

      alert('✅ Your information has been updated successfully!');
    } catch (error) {
      console.error('Error saving franchisee data:', error);
      alert('Error saving your information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Build a live preview of how the statement header will look
  const getPreviewTitle = () => {
    const suffix = formData.statementTitle.trim();
    return suffix ? `BEAUX-ARTS ${suffix} STATEMENT` : 'BEAUX-ARTS STATEMENT';
  };

  const getPreviewSubtitle = () => {
    return formData.statementSubtitle.trim() || '';
  };

  if (loading) {
    return (
      <div className="franchisee-info-container">
        <div className="loading">Loading your information...</div>
      </div>
    );
  }

  return (
    <div className="franchisee-info-container">
      <div className="franchisee-info-header">
        <h1>📝 Your Information</h1>
        <p className="subtitle">Manage your contact details, invoice and statement settings</p>
      </div>

      <form onSubmit={handleSubmit} className="franchisee-info-form">
        {/* Basic Information Section */}
        <div className="form-section">
          <h2>Basic Information</h2>
          <p className="section-note">This information is already in our system</p>
          
          <div className="form-row">
            <div className="form-group">
              <label>Your Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g., Sarah Johnson"
              />
            </div>

            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="sarah@example.com"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="082 555 1234"
              />
            </div>

            <div className="form-group">
              <label>WhatsApp Number</label>
              <input
                type="tel"
                name="whatsapp"
                value={formData.whatsapp}
                onChange={handleChange}
                placeholder="082 555 1234"
              />
              <small>Will appear on invoices/statements</small>
            </div>
          </div>
        </div>

        {/* Business Information Section */}
        <div className="form-section">
          <h2>Business Information</h2>
          <p className="section-note">This will appear on your invoices and statements</p>
          
          <div className="form-group">
            <label>Business/Trading Name</label>
            <input
              type="text"
              name="businessName"
              value={formData.businessName}
              onChange={handleChange}
              placeholder="e.g., Beaux-Arts Kidz Kraftz - Rustenburg"
            />
            <small>Leave blank to use your name</small>
          </div>

          <div className="form-group">
            <label>Physical Address</label>
            <input
              type="text"
              name="physicalAddress"
              value={formData.physicalAddress}
              onChange={handleChange}
              placeholder="e.g., 123 Main Street, Suburb"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="e.g., Rustenburg"
              />
            </div>

            <div className="form-group">
              <label>Postal Code</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                placeholder="e.g., 0299"
              />
            </div>
          </div>
        </div>

        {/* ============================================================
            STATEMENT SETTINGS SECTION (NEW)
            ============================================================ */}
        <div className="form-section statement-settings-section">
          <h2>📄 Statement / Invoice Settings</h2>
          <p className="section-note">Customise how your statements and invoices look. These are set once and apply to all statements.</p>

          <div className="form-group">
            <label>Statement Title</label>
            <div className="title-input-wrapper">
              <span className="title-prefix">BEAUX-ARTS</span>
              <input
                type="text"
                name="statementTitle"
                value={formData.statementTitle}
                onChange={handleChange}
                placeholder="e.g., POTTERY, KIDZ KRAFTZ"
              />
            </div>
            <small>Add your activity or business name after "BEAUX-ARTS". Leave blank for just "BEAUX-ARTS". E.g., type "POTTERY" to get "BEAUX-ARTS POTTERY STATEMENT".</small>
          </div>

          <div className="form-group">
            <label>Statement Subtitle (optional)</label>
            <input
              type="text"
              name="statementSubtitle"
              value={formData.statementSubtitle}
              onChange={handleChange}
              placeholder="e.g., Pottery lessons at your child's school"
            />
            <small>Appears below the title on statements. Leave blank if not needed.</small>
          </div>

          <div className="form-group">
            <label>Your Details on Statements</label>
            <p className="section-note" style={{ marginBottom: '10px' }}>Choose which of your details appear on statements</p>
            
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="showFranchiseeNameOnStatement"
                  checked={formData.showFranchiseeNameOnStatement}
                  onChange={handleChange}
                />
                <span>Show my name ({formData.name || 'not set'})</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="showFranchiseePhoneOnStatement"
                  checked={formData.showFranchiseePhoneOnStatement}
                  onChange={handleChange}
                />
                <span>Show contact number ({formData.whatsapp || formData.phone || 'not set'})</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="showFranchiseeEmailOnStatement"
                  checked={formData.showFranchiseeEmailOnStatement}
                  onChange={handleChange}
                />
                <span>Show email address ({formData.email || 'not set'})</span>
              </label>
            </div>
          </div>

          {/* Live Preview */}
          <div className="statement-preview">
            <label>Preview</label>
            <div className="preview-box">
              <div className="preview-title">{getPreviewTitle()}</div>
              {getPreviewSubtitle() && (
                <div className="preview-subtitle">{getPreviewSubtitle()}</div>
              )}
              <div className="preview-details">
                {formData.showFranchiseeNameOnStatement && formData.name && (
                  <span>{formData.name}</span>
                )}
                {formData.showFranchiseePhoneOnStatement && (formData.whatsapp || formData.phone) && (
                  <span>Contact: {formData.whatsapp || formData.phone}</span>
                )}
                {formData.showFranchiseeEmailOnStatement && formData.email && (
                  <span>{formData.email}</span>
                )}
              </div>
              <div className="preview-student-line">Student: John Smith &nbsp;|&nbsp; School: Example Primary</div>
            </div>
          </div>
        </div>

        {/* Banking Details Section */}
        <div className="form-section banking-section">
          <div className="section-header-with-toggle">
            <div>
              <h2>Banking Details (Optional)</h2>
              <p className="section-note">Include your banking details on statements for easy payments</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                name="includeBankingDetails"
                checked={formData.includeBankingDetails}
                onChange={handleChange}
              />
              <span className="toggle-slider"></span>
              <span className="toggle-label">
                {formData.includeBankingDetails ? 'Include on Statements' : 'Don\'t Include'}
              </span>
            </label>
          </div>

          {formData.includeBankingDetails && (
            <div className="banking-fields">
              <div className="form-row">
                <div className="form-group">
                  <label>Bank Name *</label>
                  <input
                    type="text"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    placeholder="e.g., ABSA"
                    required={formData.includeBankingDetails}
                  />
                </div>

                <div className="form-group">
                  <label>Account Name *</label>
                  <input
                    type="text"
                    name="accountName"
                    value={formData.accountName}
                    onChange={handleChange}
                    placeholder="e.g., Capellevid CC t/a Beaux-Arts"
                    required={formData.includeBankingDetails}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Account Number *</label>
                  <input
                    type="text"
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    placeholder="e.g., 4084543432"
                    required={formData.includeBankingDetails}
                  />
                </div>

                <div className="form-group">
                  <label>Branch Code *</label>
                  <input
                    type="text"
                    name="branchCode"
                    value={formData.branchCode}
                    onChange={handleChange}
                    placeholder="e.g., 630 510"
                    required={formData.includeBankingDetails}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Payment Reference Format</label>
                <select
                  name="referenceFormat"
                  value={formData.referenceFormat}
                  onChange={handleChange}
                >
                  <option value="{studentName}+{schoolName}">Student Name + School Name</option>
                  <option value="{accountNumber}">Account Number Only</option>
                  <option value="{studentName}">Student Name Only</option>
                </select>
                <small>This helps you identify payments</small>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={onClose} 
            className="btn-cancel"
            disabled={saving}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn-save"
            disabled={saving}
          >
            {saving ? 'Saving...' : '💾 Save Information'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default FranchiseeInformation;